import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES } from "../mock-data";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * ATELIER — Editorial Luxury Magazine
 *
 * Mood: Vogue cover meets Hermès catalog. Asymmetric oversized serif,
 * color-shifting page backgrounds, clip-path text reveals, hairline rules.
 * Every section uses a different color palette like flipping magazine pages.
 *
 * Premium techniques:
 *  - Document-level background color tween synced to scroll position
 *  - Mix of horizontal and vertical reading flow
 *  - Asymmetric grids with editorial hierarchy
 *  - Roman numeral date treatment
 *  - Decorative SVG ornaments
 *  - Hover state on entire cards (no buttons, the card IS the button)
 */
export function AtelierLayout({ formSlot, program, title, dateTime, location, description, galleryImages }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);

  // Roman numeral converter for editorial date treatment
  const toRoman = (n: number) => {
    const map: [number, string][] = [[1000,"M"],[900,"CM"],[500,"D"],[400,"CD"],[100,"C"],[90,"XC"],[50,"L"],[40,"XL"],[10,"X"],[9,"IX"],[5,"V"],[4,"IV"],[1,"I"]];
    let s = "";
    for (const [v, sym] of map) { while (n >= v) { s += sym; n -= v; } }
    return s;
  };

  const d = dateTime ? new Date(dateTime) : null;
  const monthName = d?.toLocaleString("tr-TR", { month: "long" }).toUpperCase() ?? "";
  const dayRoman = d ? toRoman(d.getDate()) : "—";
  const yearRoman = d ? toRoman(d.getFullYear()) : "—";

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.6, easing: (t) => 1 - Math.pow(1 - t, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // ── PAGE-LEVEL COLOR TWEEN ──────────────────────────────────────
      // Background morphs through editorial palette as you scroll
      const palette = [
        { bg: "#f8f4ed", fg: "#1a1a1a" },  // cream
        { bg: "#1a1a1a", fg: "#f8f4ed" },  // charcoal
        { bg: "#e8c4c4", fg: "#3a1818" },  // dusty rose
        { bg: "#0a1828", fg: "#e8d9b8" },  // midnight blue + gold
        { bg: "#f8f4ed", fg: "#1a1a1a" },  // back to cream
      ];

      palette.forEach((p, i) => {
        gsap.to("body", {
          backgroundColor: p.bg,
          color: p.fg,
          ease: "power2.inOut",
          scrollTrigger: {
            trigger: `.atl-page-${i}`,
            start: "top 60%",
            end: "bottom 60%",
            toggleActions: "play reverse play reverse",
          },
        });
      });

      // ── HERO TITLE — character-by-character with rotation ──────────
      const heroSplit = new SplitText(".atl-hero-title", { type: "chars,words" });
      gsap.from(heroSplit.chars, {
        opacity: 0,
        y: "100%",
        rotateZ: () => gsap.utils.random(-15, 15),
        stagger: { each: 0.04, from: "random" },
        duration: 1.2,
        ease: "expo.out",
        delay: 0.4,
      });

      // Issue number reveal
      gsap.from(".atl-issue", {
        opacity: 0,
        x: -20,
        duration: 1,
        delay: 1,
        ease: "power2.out",
      });

      // ── COLOPHON: kerning expands as you scroll ────────────────────
      gsap.to(".atl-colophon", {
        letterSpacing: "1em",
        ease: "none",
        scrollTrigger: {
          trigger: ".atl-page-0",
          start: "top top",
          end: "bottom top",
          scrub: 1,
        },
      });

      // ── PAGE 1: HORIZONTAL PINNED CHAPTER ──────────────────────────
      // Magazine spread that slides horizontally on vertical scroll
      const spread = document.querySelector<HTMLElement>(".atl-spread");
      const spreadInner = document.querySelector<HTMLElement>(".atl-spread-inner");
      if (spread && spreadInner) {
        const distance = spreadInner.scrollWidth - window.innerWidth;
        gsap.to(spreadInner, {
          x: -distance,
          ease: "none",
          scrollTrigger: {
            trigger: spread,
            start: "top top",
            end: `+=${distance}px`,
            pin: true,
            scrub: 1,
          },
        });
      }

      // ── PAGE 2: ROMAN NUMERAL DATE — clip-path reveal ──────────────
      gsap.from(".atl-roman", {
        clipPath: "inset(100% 0 0 0)",
        stagger: 0.15,
        duration: 1.4,
        ease: "expo.out",
        scrollTrigger: {
          trigger: ".atl-roman-section",
          start: "top 60%",
          toggleActions: "play reverse play reverse",
        },
      });

      // ── PAGE 3: STORY PARAGRAPH — line-by-line mask reveal ─────────
      const lines = document.querySelectorAll(".atl-story-line");
      lines.forEach((line) => {
        gsap.from(line, {
          clipPath: "inset(0 100% 0 0)",
          duration: 1.6,
          ease: "expo.out",
          scrollTrigger: {
            trigger: line,
            start: "top 80%",
            toggleActions: "play reverse play reverse",
          },
        });
      });

      // ── PAGE 4: VENUE CARD — scale + fade ──────────────────────────
      gsap.from(".atl-venue-frame", {
        scale: 0.92,
        opacity: 0,
        duration: 1.2,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".atl-venue-section",
          start: "top 70%",
          toggleActions: "play reverse play reverse",
        },
      });

      gsap.from(".atl-venue-line", {
        x: 60,
        opacity: 0,
        stagger: 0.1,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".atl-venue-section",
          start: "top 60%",
          toggleActions: "play reverse play reverse",
        },
      });

      // ── FORM SECTION fade ───────────────────────────────────────────
      gsap.from(".atl-form-wrapper", {
        opacity: 0,
        y: 40,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".atl-form-wrapper",
          start: "top 80%",
          toggleActions: "play reverse play reverse",
        },
      });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
      // Reset body bg/color when unmounting
      document.body.style.backgroundColor = "";
      document.body.style.color = "";
    };
  }, []);

  const storyLines = (description ?? "").split(/(?<=[.!?])\s+/).filter(Boolean);

  return (
    <main className="overflow-x-hidden" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
      {/* Top hairline — runs across entire site */}
      <div className="fixed top-0 left-0 right-0 z-50 pointer-events-none">
        <div className="atl-colophon px-8 pt-6 flex justify-between items-center text-[10px] uppercase tracking-[0.5em]" style={{ mixBlendMode: "difference", color: "white" }}>
          <span>ATELIER</span>
          <span className="atl-issue">№ {d?.getFullYear() ?? "MMXXVI"}</span>
        </div>
      </div>

      {/* ═══ PAGE 0 — HERO COVER (cream) ═══════════════════════════════ */}
      <section className="atl-page-0 relative min-h-screen flex flex-col justify-center px-6 sm:px-16 lg:px-24 py-32">
        <p className="text-[11px] uppercase tracking-[0.5em] mb-8 opacity-60">
          The Wedding Issue
        </p>

        {/* Asymmetric oversized serif title */}
        <div className="overflow-hidden">
          <h1 className="atl-hero-title font-light leading-[0.85] tracking-[-0.04em]" style={{ fontSize: "clamp(4rem, 14vw, 13rem)" }}>
            {title}
          </h1>
        </div>

        {/* Editorial-style sub info */}
        <div className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-6 text-[11px] uppercase tracking-[0.3em]">
          <div>
            <p className="opacity-40 mb-2">Tarih</p>
            <p>{monthName} {d?.getDate()}</p>
          </div>
          <div>
            <p className="opacity-40 mb-2">Yıl</p>
            <p>{d?.getFullYear()}</p>
          </div>
          <div>
            <p className="opacity-40 mb-2">Mekan</p>
            <p className="leading-snug">{location?.split(",")[0] ?? "—"}</p>
          </div>
          <div>
            <p className="opacity-40 mb-2">Bölüm</p>
            <p>I — Davet</p>
          </div>
        </div>

        {/* Decorative ornament */}
        <svg className="absolute top-1/2 right-8 -translate-y-1/2 opacity-20" width="80" height="200" viewBox="0 0 80 200">
          <line x1="40" y1="0" x2="40" y2="200" stroke="currentColor" strokeWidth="0.5" />
          <circle cx="40" cy="40" r="3" fill="currentColor" />
          <circle cx="40" cy="100" r="6" fill="none" stroke="currentColor" strokeWidth="0.5" />
          <circle cx="40" cy="160" r="3" fill="currentColor" />
        </svg>
      </section>

      {/* ═══ PAGE 1 — HORIZONTAL SPREAD (charcoal) ════════════════════ */}
      <section className="atl-page-1 atl-spread relative h-screen overflow-hidden">
        <div className="atl-spread-inner absolute inset-0 flex items-center" style={{ width: "max-content" }}>
          {/* Panel A: large quote */}
          <div className="flex-shrink-0 w-screen flex items-center justify-center px-16">
            <div className="max-w-2xl">
              <p className="text-[10px] uppercase tracking-[0.5em] opacity-50 mb-12">— Bölüm II —</p>
              <p className="font-light italic leading-[1.1] tracking-tight" style={{ fontSize: "clamp(2rem, 5vw, 4.5rem)" }}>
                "İki kalbin tek bir hikâyesi başlıyor."
              </p>
            </div>
          </div>

          {/* Panel B: full-bleed image */}
          <div className="flex-shrink-0 h-screen" style={{ width: "60vw" }}>
            <img src={galleryImages[0]} alt="" className="w-full h-full object-cover" />
          </div>

          {/* Panel C: editorial copy */}
          <div className="flex-shrink-0 w-screen flex items-center px-16">
            <div className="max-w-md">
              <p className="text-[10px] uppercase tracking-[0.5em] opacity-50 mb-4">An Editorial</p>
              <h2 className="font-light leading-tight mb-8" style={{ fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
                Bir aşk, iki şehir, sayısız hatıra.
              </h2>
              <p className="opacity-70 leading-relaxed text-sm tracking-wide">
                {description?.slice(0, 220) ?? ""}…
              </p>
            </div>
          </div>

          {/* Panel D: split image grid */}
          <div className="flex-shrink-0 h-screen flex" style={{ width: "70vw" }}>
            <div className="w-1/2 h-full"><img src={galleryImages[1]} alt="" className="w-full h-full object-cover" /></div>
            <div className="w-1/2 h-full"><img src={galleryImages[2]} alt="" className="w-full h-full object-cover" /></div>
          </div>
        </div>
      </section>

      {/* ═══ PAGE 2 — ROMAN NUMERAL DATE (dusty rose) ═════════════════ */}
      <section className="atl-page-2 atl-roman-section relative min-h-screen flex flex-col justify-center px-6 sm:px-16 py-32">
        <p className="text-[11px] uppercase tracking-[0.5em] mb-16 opacity-60">— Bölüm III · La Data —</p>

        <div className="grid grid-cols-3 gap-4 sm:gap-8 items-center">
          <div className="atl-roman text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] opacity-50 mb-4">Gün</p>
            <p className="font-light leading-none" style={{ fontSize: "clamp(4rem, 14vw, 11rem)" }}>{dayRoman}</p>
          </div>
          <div className="atl-roman text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] opacity-50 mb-4">Ay</p>
            <p className="font-light leading-none italic" style={{ fontSize: "clamp(1.8rem, 6vw, 4.5rem)" }}>{monthName}</p>
          </div>
          <div className="atl-roman text-center">
            <p className="text-[10px] uppercase tracking-[0.3em] opacity-50 mb-4">Yıl</p>
            <p className="font-light leading-none" style={{ fontSize: "clamp(2rem, 7vw, 5.5rem)" }}>{yearRoman}</p>
          </div>
        </div>

        {d && (
          <p className="text-center mt-20 text-[11px] tracking-[0.4em] uppercase opacity-50">
            {d.toLocaleDateString("tr-TR", { weekday: "long" })} · {d.toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" })}
          </p>
        )}
      </section>

      {/* ═══ PAGE 3 — STORY (midnight + gold) ══════════════════════════ */}
      <section className="atl-page-3 relative min-h-screen flex items-center px-6 sm:px-16 py-32">
        <div className="max-w-3xl mx-auto">
          <p className="text-[11px] uppercase tracking-[0.5em] mb-16 opacity-60" style={{ color: "#e8d9b8" }}>— Bölüm IV · Hikaye —</p>
          {storyLines.map((line, i) => (
            <p
              key={i}
              className="atl-story-line font-light leading-[1.4] mb-6 italic"
              style={{ fontSize: "clamp(1.25rem, 2.2vw, 1.875rem)" }}
            >
              {line.trim()}
            </p>
          ))}
          <div className="mt-12 text-center">
            <svg width="80" height="20" viewBox="0 0 80 20" className="inline-block opacity-50">
              <line x1="0" y1="10" x2="30" y2="10" stroke="currentColor" strokeWidth="0.5" />
              <circle cx="40" cy="10" r="2" fill="currentColor" />
              <line x1="50" y1="10" x2="80" y2="10" stroke="currentColor" strokeWidth="0.5" />
            </svg>
          </div>
        </div>
      </section>

      {/* ═══ PAGE 4 — VENUE + FORM (back to cream) ═════════════════════ */}
      <section className="atl-page-4 atl-venue-section relative min-h-screen flex flex-col items-center justify-center px-6 sm:px-16 py-32">
        <p className="text-[11px] uppercase tracking-[0.5em] mb-12 opacity-60">— Bölüm V · Mekan —</p>

        <div className="atl-venue-frame border border-current/30 p-12 sm:p-16 max-w-xl text-center mb-24">
          <p className="atl-venue-line text-[10px] uppercase tracking-[0.4em] opacity-50 mb-6">L'invito</p>
          <h2 className="atl-venue-line font-light italic leading-tight mb-6" style={{ fontSize: "clamp(2rem, 4.5vw, 3.5rem)" }}>
            {location ?? "—"}
          </h2>
          <p className="atl-venue-line text-sm uppercase tracking-[0.3em] opacity-60">
            {d?.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })}
          </p>
        </div>

        <div className="atl-form-wrapper max-w-md w-full">
          <p className="text-center text-[10px] uppercase tracking-[0.5em] opacity-50 mb-4">RSVP</p>
          <h3 className="text-center font-light italic mb-10" style={{ fontSize: "clamp(1.75rem, 3vw, 2.5rem)" }}>
            Cevabınız
          </h3>
          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
